"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CartClient = void 0;
const lodash_1 = require("lodash");
const commerce_core_1 = require("@broadleaf/commerce-core");
const types_1 = require("./types");
/**
 * Client used for interacting with the cart management APIs.
 */
class CartClient extends commerce_core_1.AbstractClient {
    constructor(options) {
        super({ ...options, basePath: options.basePath || '/api/cart-operations' });
    }
    /**
     * Resolve the authenticated customer's cart
     *
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async resolveCart(options) {
        const config = {
            method: 'get',
            url: `/resolve-cart`,
            params: {
                status: options?.statuses
            }
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Create a new cart using a specified locale and currency
     *
     * @param cartCreationRequest the request used to create a new cart
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async createCart(cartCreationRequest, options) {
        const config = {
            method: 'post',
            url: `/cart`,
            data: cartCreationRequest
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Updates basic info on the cart like its name or, if a quote, its expiration date.
     * This is only used to update non-pricing info on the cart not possible to update through
     * other endpoints.
     *
     * @param cartId Id of the cart to update
     * @param request Request containing the date to update with
     * @param options the options for the HTTP call
     * @returns The updated cart
     */
    async updateCartInfo(cartId, request, options) {
        const config = {
            method: 'patch',
            url: `/cart/${cartId}/info`,
            data: request
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieve a cart by id
     *
     * @param cartId the id of the cart to retrieve
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async getCart(cartId, options) {
        const config = {
            method: 'get',
            url: `/cart/${cartId}`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Method to read all of a user's non-submitted or -locked carts.
     *
     * @param customerId Id of the authenticated user
     * @param accountId Id of the selected account the user is shopping withing
     * @param cq RSQL parameters
     * @param options Additional request configuration
     *
     * @returns The page of carts.
     */
    async getAllCarts(customerId, accountId, cq, options) {
        const config = {
            method: 'get',
            url: `/cart`,
            params: {
                accountId,
                all: true,
                customerId,
                cq
            }
        };
        this.attachPageableParamsIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Change the specified cart to be the default/implicit cart. This is the cart usually acted
     * against by the user and has no name.
     *
     * @param cartId Id of the cart to change
     * @param options Additional request config
     * @returns The updated cart
     */
    async makeCartDefault(cartId, options) {
        const config = {
            method: 'post',
            url: `/cart/${cartId}/make-default`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Method used to change the cart's status. Typically this is used to reclaim a
     * requires-approval or rejected cart in B2B flows.
     *
     * @param cartId Id of the cart to change
     * @param status The new status
     * @param options Additional request config
     * @returns The updated cart
     */
    async changeCartStatus(cartId, status, options) {
        const config = {
            method: 'patch',
            url: `/cart/${cartId}/status`,
            data: {
                status
            }
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * This is used to reclaim a requires-approval or rejected cart in B2B flows.
     *
     * @param cartId Id of the cart to change
     * @param options Additional request config
     * @returns The updated cart
     */
    async reclaimCart(cartId, options) {
        return await this.changeCartStatus(cartId, types_1.DefaultCartStatus.IN_PROCESS, options);
    }
    /**
     * Transfer an anonymous cart to the authenticated customer
     *
     * @param cartId the id of the cart to transfer
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async transferCart(cartId, options) {
        const config = {
            method: 'post',
            url: `/cart/${cartId}/transfer`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Price the cart by id
     *
     * @param cartId the id of the cart to price
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async priceCart(cartId, options) {
        const priceRequest = {};
        if (options?.locale) {
            priceRequest.locale = options?.locale;
        }
        if (options?.currency) {
            priceRequest.currency = options?.currency;
        }
        const config = {
            method: 'post',
            url: `/cart/${cartId}/pricing`,
            data: priceRequest
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Add an item to the cart
     *
     * @param cartId the id of the cart to add to
     * @param addItemRequest the item request to add
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async addItemToCart(cartId, addItemRequest, options) {
        const config = {
            method: 'post',
            url: `/cart/${cartId}/items`,
            data: addItemRequest
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Update an item in the cart
     *
     * @param cartId the id of the cart
     * @param updateItemRequest the item request to update
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async updateItemInCart(cartId, updateItemRequest, options) {
        const { cartItemId } = updateItemRequest;
        const config = {
            method: 'patch',
            url: `/cart/${cartId}/items/${cartItemId}`,
            data: updateItemRequest
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Remove an item from the cart
     *
     * @param cartId the id of the cart
     * @param cartItemId the id of the cart item to remove
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async removeItemFromCart(cartId, cartItemId, options) {
        const config = {
            method: 'delete',
            url: `/cart/${cartId}/items/${cartItemId}`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Add a list of items to the cart
     *
     * @param cartId the id of the cart to add to
     * @param addItemRequests the list of item request to add
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async addManyItemsToCart(cartId, addItemRequests, options) {
        const config = {
            method: 'post',
            url: `/cart/${cartId}/bulk-items`,
            data: addItemRequests
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Remove many items from the cart
     *
     * @param cartId the id of the cart
     * @param cartItemIds the list of ids to remove from the cart
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async removeManyItemsFromCart(cartId, cartItemIds, options) {
        const config = {
            method: 'delete',
            url: `/cart/${cartId}/bulk-items`,
            params: { cartItemIds }
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Add item lists to the cart
     *
     * @param cartId the id of the cart to add to
     * @param itemListIds the item list ids to add to the cart
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async addItemListsToCart(cartId, itemListIds, options) {
        const config = {
            method: 'post',
            url: `/cart/${cartId}/item-lists`,
            data: { itemListIds }
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Add an offer code to the cart
     *
     * @param cartId the id of the cart to add to
     * @param offerCode the offer code to add
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async addOfferCodeToCart(cartId, offerCode, options) {
        const config = {
            method: 'post',
            url: `/cart/${cartId}/offer-codes`,
            data: { code: offerCode }
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Remove an offer code from the cart
     *
     * @param cartId the id of the cart
     * @param offerCode the offer code to remove
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async removeOfferCodeFromCart(cartId, offerCode, options) {
        const config = {
            method: 'delete',
            url: `/cart/${cartId}/offer-codes/${offerCode}`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Add an attribute to the cart
     *
     * @param cartId the id of the cart to add to
     * @param addAttributeRequest the attribute to add to the cart
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async addAttributeToCart(cartId, addAttributeRequest, options) {
        const config = {
            method: 'post',
            url: `/cart/${cartId}/attributes`,
            data: addAttributeRequest
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Remove an attribute from the cart
     *
     * @param cartId the id of the cart to remove from
     * @param attributeKey the attribute to remove from the cart
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async removeAttributeFromCart(cartId, attributeKey, options) {
        const config = {
            method: 'delete',
            url: `/cart/${cartId}/attributes/${attributeKey}`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * For carts in the {@code AWAITING_PAYMENT_FINALIZATION} status, this
     * method updates the cart status to {@code IN_PROCESS} allowing the
     * customer to once again edit the cart.
     *
     * @param cartId the id of the cart
     * @param options the options to the HTTP call
     */
    async releasePaymentFinalizationLock(cartId, options) {
        const config = {
            method: 'patch',
            url: `/cart/${cartId}/release-payment-finalization-lock`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachGuestTokenHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieve a historical cart for the authenticated customer
     *
     * @param orderNumber the order number of the cart to retrieve
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async getHistoricalCart(orderNumber, options) {
        const config = {
            method: 'get',
            url: `/cart-history/${orderNumber}`
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieve a historical cart for an anonymous customer
     *
     * @param orderNumber the order number of the cart to retrieve
     * @param emailAddress the contact email address on the cart
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async getAnonymousHistoricalCart(orderNumber, emailAddress, options) {
        const config = {
            method: 'get',
            url: `/cart-history/${orderNumber}`,
            params: {
                emailAddress
            }
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieve a historical cart by ID for the authenticated customer.
     * This method only returns the cart with "AWAITING_PAYMENT_FINALIZATION" or "SUBMITTED" status.
     *
     * @param cartId the cart id of the cart to retrieve
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async getHistoricalCustomerCartById(cartId, options) {
        const config = {
            method: 'get',
            url: `/cart-history`,
            params: {
                cartId
            }
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieve a cart by ID for an anonymous customer.
     * This method only returns the cart with "AWAITING_PAYMENT_FINALIZATION" or "SUBMITTED" status.
     *
     * @param cartId the cart id of the cart to retrieve
     * @param emailAddress the contact email address on the cart
     * @param options the options to the HTTP call
     * @return a single Cart
     */
    async getAnonymousHistoricalCartById(cartId, emailAddress, options) {
        const config = {
            method: 'get',
            url: `/cart-history`,
            params: {
                cartId,
                emailAddress
            }
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Gets the fulfillment options available for the items in the specified cart.
     * This includes options for both per-item and per-group fulfillment strategies.
     *
     * @param cartId Id of the cart the items are within
     * @param options Additional request config
     * @returns Response with fulfillment options available for the items in the specified cart.
     */
    async getFulfillmentOptionsForCartItems(cartId, options) {
        const config = {
            method: 'get',
            url: `/fulfillment-options/${cartId}`
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Sends a request to set the preliminary fulfillment option for a fulfillment item or group in the cart.
     * This can be changed during checkout.
     *
     * @param cartId Id of the Cart the fulfillment option applies to
     * @param option The option that was selected and the reference number of the fulfillment item or group
     *     it applies to.
     * @param options Additional request config.
     * @returns The updated cart and available fulfillment options.
     */
    async selectFulfillmentOption(cartId, option, options) {
        const config = {
            method: 'post',
            url: `/fulfillment-options/${cartId}/select`,
            data: option
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Gets the fulfillment options available for the contents of the cart. May include the preliminary
     * shipping address in the request.
     *
     * @param cartId ID of the cart containing the fulfillment group
     * @param address Address for determining fulfillment options for the group
     * @param options Additional request config.
     * @returns The fulfillment options available for the contents of the cart.
     */
    async determineOptionsAndGroups(cartId, address, options) {
        const config = {
            method: 'post',
            url: `/fulfillment-options/${cartId}`,
            data: address
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Sets a preliminary shipping address for a fulfillment group.
     *
     * @param cartId Id of the cart containing the fulfillment group
     * @param request Request containing the fulfillment group reference number and the address.
     * @param options Additional request config.
     * @returns The updated cart and available fulfillment options
     */
    async updateFulfillmentGroupAddress(cartId, request, options) {
        const config = {
            method: 'post',
            url: `/fulfillment-options/${cartId}/address/${request.fulfillmentGroupReference}`,
            data: request.address
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Gets the combined total fulfillment price per service level of fulfillment options selected, e.g.,
     * the total price for Standard Shipping based on what items or groups it is selected for.
     *
     * @param cartId Id of the cart containing the fulfillment group(s)
     * @param options Additional request config.
     * @returns A map of service-level to combined total.
     */
    async getTotalsForServiceLevels(cartId, options) {
        const config = {
            method: 'get',
            url: `/fulfillment-options/${cartId}/service-levels`
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieves the Fulfillment Pricing Config that informs the caller of the fulfillment pricing
     * strategy (per group or item) to use and default service level.
     *
     * @param options Additional request config.
     * @returns The Fulfillment Pricing Config to use.
     */
    async getPricingConfig(options) {
        const config = {
            method: 'get',
            url: `/fulfillment-options/pricing-config`
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieves all action audits for a cart.
     *
     * @param cartId Id of the parent cart
     * @param options Additional request configuration including pagination params.
     * @returns A page of action audits
     */
    async readAllActionAuditForCart(cartId, options) {
        const config = {
            method: 'get',
            url: `/cart-action-audit/${cartId}`
        };
        this.attachPageableParamsIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Attaches the cart version header to the request `config` if present on `options`.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachCartVersionHeaderIfPresent(config, options) {
        if (options?.version !== undefined) {
            if (!config.headers) {
                config.headers = {};
            }
            config.headers[types_1.CartRequestHeaderType.CART_VERSION] = String(options.version);
        }
    }
    /**
     * Attaches the guest token header to the request `config` if present on `options`.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachGuestTokenHeaderIfPresent(config, options) {
        if (options?.guestToken !== undefined) {
            if (!config.headers) {
                config.headers = {};
            }
            config.headers[types_1.CartRequestHeaderType.GUEST_TOKEN] = options.guestToken;
        }
    }
    /**
     * Attaches the cart status param to the request `config` if present on `options`. This will filter the result by the status.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachCartStatusParamIfPresent(config, options) {
        if (options?.statuses) {
            if (!config.params) {
                config.params = {};
            }
            config.params.status = options.statuses;
        }
    }
    /**
     * Attaches the price flag param to the request `config` if present on `options`. This indicates that the cart should be repriced.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachPriceParamIfPresent(config, options) {
        if (options?.price !== undefined) {
            if (!config.params) {
                config.params = {};
            }
            config.params.price = options.price;
        }
    }
    /**
     * Attaches pageable params (size, sort, etc.) to the request `config` if present on `options`.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachPageableParamsIfPresent(config, options) {
        if (options) {
            const { page, size, sort } = options;
            if (page || page === 0) {
                (0, lodash_1.set)(config, 'params.page', page);
            }
            if (size || size === 0) {
                (0, lodash_1.set)(config, 'params.size', size);
            }
            if (sort) {
                (0, lodash_1.set)(config, 'params.sort', sort);
            }
        }
    }
}
exports.CartClient = CartClient;
//# sourceMappingURL=cart-client.js.map